<script setup lang="ts">
import i18next from 'i18next';

import VButton from '@components/buttons/VButton/VButton.vue';
import { TRANSLATION_GROUP_CHANGE_HISTORY } from '@modules/change-history/constants';
import { useFormatDate } from '@modules/core/composables/datetime';
import { useConfirmDialogs } from '@modules/core/composables/dialogs';
import { EAasmState } from '@modules/core/enums';
import { scrollElementIntoView } from '@modules/core/utils/scrollElementIntoView';
import { useI18n } from '@modules/i18n/composables';

import type {
  IChangeHistoryEmits,
  IChangeHistoryEntry,
  IChangeHistoryProps,
  IChangeHistorySlots,
} from './types';

defineProps<IChangeHistoryProps>();

const emit = defineEmits<IChangeHistoryEmits>();
defineSlots<IChangeHistorySlots>();

const translationPath = TRANSLATION_GROUP_CHANGE_HISTORY;
const { getTranslation } = useI18n();
const { formatDate } = useFormatDate();
const { confirmDelete } = useConfirmDialogs();

function handleEdit(entry: IChangeHistoryEntry): void {
  emit('edit', entry);

  setTimeout(() => {
    scrollElementIntoView(`#history-item-${entry.id}`, '#modal-scroll-container');
  }, 100);
}

function handleDelete(event: MouseEvent, entry: IChangeHistoryEntry): void {
  confirmDelete({
    event,
    message: getTranslation(translationPath, 'MODAL_QUESTION').value,
    acceptLabel: getTranslation(translationPath, 'MODAL_ANSWER_YES').value,
    rejectLabel: getTranslation(translationPath, 'MODAL_ANSWER_NO').value,
    accept: () => emit('delete', entry),
  });
}
</script>

<template>
  <PCard v-data-qa-auto-id="'VChangeHistory'"
    :pt="{
      body: { class: 'p-0' },
      title: { class: 'p-4 mb-0' },
      content: { class: 'p-0' },
    }"
  >
    <template #title>
      <div class="flex flex-row items-center">
        <i class="pi pi-clock mr-2 text-xl" />
        <span>
          {{ $getTranslation(translationPath, 'HEADER') }}
        </span>
      </div>
    </template>
    <template #content>
      <div class="border-t">
        <template v-if="list?.length">
          <div
            v-for="item in list"
            :id="`history-item-${item.id}`"
            :key="item.id"
            class="flex flex-col border-b border-gray-200 last:border-0"
            :class="{ 'bg-primary/[0.05]': item.active }"
          >
            <div class="flex flex-col px-6 py-2 sm:flex-row sm:items-center">
              <span class="mb-4 whitespace-nowrap text-muted-color sm:mb-0 sm:mr-6">
                {{ formatDate(item.created_at, 'P p', i18next.language) }}
              </span>
              <span class="mb-4 grow sm:mb-0"> {{ $t(item.message.text, item.message.data) }}</span>
              <span class="flex min-h-[36px] flex-row items-center justify-between gap-2 sm:justify-normal">
                <VButton
                  v-if="editPermission && $hasPermission(editPermission)"
                  :label="$getTranslation(translationPath, 'EDIT')"
                  text
                  @click="handleEdit(item)"
                />
                <VButton
                  v-if="deletePermission && $hasPermission(deletePermission) && item?.aasm_state !== EAasmState.INIT"
                  :label="$getTranslation(translationPath, 'REMOVE')"
                  text
                  @click="handleDelete($event, item)"
                />
              </span>
            </div>
            <div v-if="item.active">
              <slot />
            </div>
          </div>
        </template>
        <div
          v-else
          class="p-4 text-muted-color"
        >
          {{ $getTranslation(translationPath, 'EMPTY_DISPLAY') }}
        </div>
      </div>
    </template>
  </PCard>
</template>
